import * as React from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A single search match within the PDF text layer. */
type PdfSearchMatch = {
  pageNumber: number;
  range: Range;
};

/** Parameters accepted by the `usePdfSearch` hook. */
type UsePdfSearchParams = {
  containerRef: React.RefObject<HTMLDivElement | null>;
  scale?: number;
};

/** Return value of the `usePdfSearch` hook. */
type UsePdfSearchReturn = {
  isOpen: boolean;
  query: string;
  setQuery: (q: string) => void;
  open: () => void;
  close: () => void;
  matches: PdfSearchMatch[];
  activeIndex: number;
  goToNext: () => void;
  goToPrevious: () => void;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Whether the CSS Custom Highlight API is available in the current browser. */
function supportsHighlightApi(): boolean {
  return typeof CSS !== "undefined" && "highlights" in CSS;
}

/**
 * Removes all `<mark>` elements with the given class name inside a container,
 * restoring the original text nodes. Used as the fallback cleanup when the
 * CSS Custom Highlight API is not available.
 */
function removeMarks({
  container,
  className,
}: {
  container: HTMLElement;
  className: string;
}): void {
  const marks = container.querySelectorAll(`mark.${className}`);
  marks.forEach((mark) => {
    const parent = mark.parentNode;
    if (!parent) return;

    const textNode = document.createTextNode(mark.textContent ?? "");
    parent.replaceChild(textNode, mark);
    parent.normalize();
  });
}

/**
 * Clears all highlights and fallback marks from the container.
 */
function clearAllHighlights({
  container,
}: {
  container: HTMLElement | null;
}): void {
  if (supportsHighlightApi()) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const highlights = (CSS as any).highlights as Map<string, unknown>;
    highlights.delete("pdf-search-highlight");
    highlights.delete("pdf-search-highlight-active");
  }

  if (container) {
    removeMarks({ container, className: "pdf-search-mark" });
    removeMarks({ container, className: "pdf-search-mark-active" });
  }
}

/**
 * Finds all occurrences of `query` (case-insensitive) within `.textLayer span`
 * elements inside the given container, returning a list of matches with their
 * page number and a DOM Range covering the matched substring.
 */
function findMatches({
  container,
  query,
}: {
  container: HTMLElement;
  query: string;
}): PdfSearchMatch[] {
  const results: PdfSearchMatch[] = [];
  const lowerQuery = query.toLowerCase();
  const spans = container.querySelectorAll<HTMLSpanElement>(".textLayer span");

  for (const span of spans) {
    const textContent = span.textContent;
    if (!textContent) continue;

    const lowerText = textContent.toLowerCase();
    const textNode = span.firstChild;
    if (!textNode || textNode.nodeType !== Node.TEXT_NODE) continue;

    const pageEl = span.closest("[data-page-number]");
    const pageNumber = pageEl
      ? Number(pageEl.getAttribute("data-page-number"))
      : 0;

    let startIndex = 0;
    while (true) {
      const matchIndex = lowerText.indexOf(lowerQuery, startIndex);
      if (matchIndex === -1) break;

      const range = document.createRange();
      range.setStart(textNode, matchIndex);
      range.setEnd(textNode, matchIndex + query.length);

      results.push({ pageNumber, range });
      startIndex = matchIndex + 1;
    }
  }

  return results;
}

/**
 * Applies CSS Custom Highlight API highlights for all matches and the active match.
 */
function applyHighlightApi({
  matches,
  activeIndex,
}: {
  matches: PdfSearchMatch[];
  activeIndex: number;
}): void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const highlights = (CSS as any).highlights as Map<string, unknown>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const HighlightCtor = (globalThis as any).Highlight;

  const allRanges = matches.map((m) => m.range);
  highlights.set("pdf-search-highlight", new HighlightCtor(...allRanges));

  if (matches[activeIndex]) {
    highlights.set(
      "pdf-search-highlight-active",
      new HighlightCtor(matches[activeIndex].range),
    );
  } else {
    highlights.delete("pdf-search-highlight-active");
  }
}

/**
 * Applies fallback `<mark>` wrapping for all matches and the active match
 * when the CSS Custom Highlight API is not available.
 *
 * Processes matches in reverse document order so that earlier ranges remain
 * valid while later ones are wrapped.
 */
function applyFallbackMarks({
  matches,
  activeIndex,
}: {
  matches: PdfSearchMatch[];
  activeIndex: number;
}): void {
  // Process in reverse order to preserve earlier range offsets.
  for (let i = matches.length - 1; i >= 0; i--) {
    const match = matches[i];
    const mark = document.createElement("mark");
    mark.className =
      i === activeIndex ? "pdf-search-mark-active" : "pdf-search-mark";

    try {
      match.range.surroundContents(mark);
    } catch {
      // Range may span multiple nodes; extract and re-wrap.
      const fragment = match.range.extractContents();
      mark.appendChild(fragment);
      match.range.insertNode(mark);
    }
  }
}

/**
 * Scrolls the active match into view. For the Highlight API path a temporary
 * element is created at the range position; for fallback mode the mark element
 * itself is scrolled.
 */
function scrollToActiveMatch({
  matches,
  activeIndex,
  container,
}: {
  matches: PdfSearchMatch[];
  activeIndex: number;
  container: HTMLElement;
}): void {
  const match = matches[activeIndex];
  if (!match) return;

  if (supportsHighlightApi()) {
    const rect = match.range.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const scrollTop =
      container.scrollTop +
      (rect.top - containerRect.top) -
      containerRect.height / 2;
    container.scrollTo({ top: scrollTop, behavior: "smooth" });
  } else {
    const activeMark = container.querySelector<HTMLElement>(
      "mark.pdf-search-mark-active",
    );
    if (activeMark) {
      activeMark.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Provides text search functionality within a PDF viewer.
 *
 * Iterates over `.textLayer span` elements inside the provided container,
 * builds a list of matches for the current query, and highlights them using
 * the CSS Custom Highlight API when available or `<mark>` elements as a
 * fallback. Exposes navigation helpers to cycle through matches.
 *
 * @param params.containerRef - Ref to the scrollable container holding the PDF pages.
 * @param params.scale - Current zoom scale; search re-executes when it changes.
 */
export function usePdfSearch({
  containerRef,
  scale,
}: UsePdfSearchParams): UsePdfSearchReturn {
  const [isOpen, setIsOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [debouncedQuery, setDebouncedQuery] = React.useState("");
  const [matches, setMatches] = React.useState<PdfSearchMatch[]>([]);
  const [activeIndex, setActiveIndex] = React.useState(0);

  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce query changes by 200ms.
  React.useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(query);
    }, 200);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  // Track previous scale to detect zoom changes.
  const prevScaleRef = React.useRef(scale);

  // Execute search when debounced query or scale changes.
  // When scale changes, react-pdf re-renders text layers asynchronously,
  // so we delay the search to let the new spans appear in the DOM.
  React.useEffect(() => {
    const container = containerRef.current;
    const scaleChanged = prevScaleRef.current !== scale;
    prevScaleRef.current = scale;

    const runSearch = () => {
      const currentContainer = containerRef.current;

      clearAllHighlights({ container: currentContainer });

      if (!currentContainer || !debouncedQuery) {
        setMatches([]);
        setActiveIndex(0);
        return;
      }

      const found = findMatches({ container: currentContainer, query: debouncedQuery });
      setMatches(found);
      setActiveIndex(0);

      if (found.length === 0) return;

      if (supportsHighlightApi()) {
        applyHighlightApi({ matches: found, activeIndex: 0 });
      } else {
        applyFallbackMarks({ matches: found, activeIndex: 0 });
      }

      scrollToActiveMatch({ matches: found, activeIndex: 0, container: currentContainer });
    };

    if (scaleChanged && debouncedQuery) {
      clearAllHighlights({ container });

      // Wait for react-pdf to re-render text layers after scale change.
      // Use a debounced MutationObserver: run the search only after DOM
      // mutations have settled (no new changes for 300ms), meaning all
      // pages finished rendering their text layers.
      if (!container) return;

      let done = false;
      let debounceTimer: ReturnType<typeof setTimeout> | null = null;

      const maxTimeout = setTimeout(() => {
        if (!done) {
          done = true;
          if (debounceTimer) clearTimeout(debounceTimer);
          observer.disconnect();
          runSearch();
        }
      }, 5000);

      const observer = new MutationObserver(() => {
        if (done) return;
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          if (!done) {
            done = true;
            observer.disconnect();
            clearTimeout(maxTimeout);
            runSearch();
          }
        }, 300);
      });

      observer.observe(container, { childList: true, subtree: true });

      return () => {
        done = true;
        observer.disconnect();
        clearTimeout(maxTimeout);
        if (debounceTimer) clearTimeout(debounceTimer);
      };
    }

    runSearch();
  }, [debouncedQuery, scale, containerRef]);

  // Update active highlight when activeIndex changes (without re-searching).
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container || matches.length === 0) return;

    if (supportsHighlightApi()) {
      applyHighlightApi({ matches, activeIndex });
    } else {
      // Remove previous active mark class and apply to new one.
      const prevActive = container.querySelector<HTMLElement>(
        "mark.pdf-search-mark-active",
      );
      if (prevActive) {
        prevActive.className = "pdf-search-mark";
      }

      const allMarks = container.querySelectorAll<HTMLElement>(
        "mark.pdf-search-mark",
      );
      if (allMarks[activeIndex]) {
        allMarks[activeIndex].className = "pdf-search-mark-active";
      }
    }

    scrollToActiveMatch({ matches, activeIndex, container });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  // Cleanup on unmount.
  React.useEffect(() => {
    return () => {
      clearAllHighlights({ container: containerRef.current });

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const open = React.useCallback(() => {
    setIsOpen(true);
  }, []);

  const close = React.useCallback(() => {
    setQuery("");
    setDebouncedQuery("");
    setMatches([]);
    setActiveIndex(0);
    clearAllHighlights({ container: containerRef.current });
    setIsOpen(false);
  }, [containerRef]);

  const setQueryWrapped = React.useCallback((q: string) => {
    setQuery(q);
  }, []);

  const goToNext = React.useCallback(() => {
    setActiveIndex((prev) => {
      if (matches.length === 0) return 0;
      return (prev + 1) % matches.length;
    });
  }, [matches.length]);

  const goToPrevious = React.useCallback(() => {
    setActiveIndex((prev) => {
      if (matches.length === 0) return 0;
      return (prev - 1 + matches.length) % matches.length;
    });
  }, [matches.length]);

  return {
    isOpen,
    query,
    setQuery: setQueryWrapped,
    open,
    close,
    matches,
    activeIndex,
    goToNext,
    goToPrevious,
  };
}
